import { Routes } from '@angular/router';
import { MainLayoutComponent } from './shared/layout/main-layout/main-layout.component';
import { authGuard, platformAdminGuard, tenantOnlyGuard } from './core/guards/auth.guard';
import { needsTenantGuard }           from './core/guards/needs-tenant.guard';
import { tenantSettingsGuard }        from './core/guards/tenant-settings.guard';
import { securityPostureGuard }        from './core/guards/security-posture.guard';

export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

  // Legacy login route - safety net; Keycloak handles the real flow
  {
    path: 'login',
    loadComponent: () =>
      import('./features/login/login.component').then(m => m.LoginComponent),
  },

  // Lives outside the protected shell: the user has zero tenant membership
  // here, and routing inside would tripwire `needsTenantGuard`.
  {
    path: 'onboarding',
    loadChildren: () =>
      import('./features/onboarding/onboarding.routes').then(m => m.onboardingRoutes),
  },

  // Registered before the protected shell so it matches without firing
  // `needsTenantGuard` - the recipient may have zero tenant membership yet.
  {
    path: 'invite',
    loadComponent: () =>
      import('./features/invite-landing/invite-landing.component').then(m => m.InviteLandingComponent),
    title: 'Accept invitation - Nexus',
  },

  // Reached when the active-tenant resolution interceptor catches a 412
  // `needs_active_tenant`. Top-level: the caller may have no active tenant yet.
  {
    path: 'tenant-picker',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/tenant-picker/tenant-picker.component').then(m => m.TenantPickerComponent),
    title: 'Select your company - Nexus',
  },

  // Phase 3 T3 - caller's pending invitations inbox. Top-level so users
  // without an active tenant can still reach it.
  {
    path: 'invitations',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/invitations/invitations.component').then(m => m.InvitationsComponent),
    title: 'Invitations - Nexus',
  },

  // Protected shell - the domain is entirely admin, so no /admin prefix needed.
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [needsTenantGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        canActivate: [tenantOnlyGuard],
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
      },
      {
        path: 'tenants',
        canActivate: [platformAdminGuard],
        loadComponent: () =>
          import('./features/tenants/tenants.component').then(m => m.TenantsComponent),
      },
      // Phase 3.1 M6 - caller-scoped list of tenants the user belongs to.
      // Distinct from the platform admin /tenants above.
      {
        path: 'me/tenants',
        loadComponent: () =>
          import('./features/tenants/my-tenants/my-tenants.component').then(m => m.MyTenantsComponent),
        title: 'My tenants - Nexus',
      },
      // Phase 3 T3 - per-tenant settings (currently: invite member).
      {
        path: 'tenants/:id/settings',
        canActivate: [tenantSettingsGuard],
        loadComponent: () =>
          import('./features/tenants/tenant-settings/tenant-settings.component').then(m => m.TenantSettingsComponent),
        title: 'Tenant settings - Nexus',
      },
      // Phase 3.1 M2 - tenant members management
      {
        path: 'tenants/:id/members',
        loadComponent: () =>
          import('./features/tenants/tenant-members/tenant-members.component').then(m => m.TenantMembersComponent),
        title: 'Members - Nexus',
      },
      {
        path: 'users',
        canActivate: [platformAdminGuard],
        loadComponent: () =>
          import('./features/users/users.component').then(m => m.UsersComponent),
      },
      {
        path: '403',
        loadComponent: () =>
          import('./shared/components/forbidden/forbidden.component').then(
            (m) => m.ForbiddenComponent,
          ),
        title: '403 Forbidden - Nexus',
      },
      {
        path: 'projects',
        loadComponent: () =>
          import('./features/projects/projects.component').then(m => m.ProjectsComponent),
      },
      // Phase 3.1 M3 - project members management
      {
        path: 'projects/:id/members',
        loadComponent: () =>
          import('./features/projects/project-members/project-members.component').then(m => m.ProjectMembersComponent),
        title: 'Project members - Nexus',
      },
      // Phase 3.11 - personal environment (own view).
      // /me must come BEFORE /:userId so the literal "me" is not captured as a userId param.
      {
        path: 'projects/:id/personal-environments/me',
        loadComponent: () =>
          import('./features/projects/dev-env/dev-env-page.component').then(m => m.DevEnvPageComponent),
        title: 'My Personal Environment - Nexus',
      },
      // Phase 3.11 - manager read-only view of a member's personal environment.
      {
        path: 'projects/:id/personal-environments/:userId',
        loadComponent: () =>
          import('./features/projects/dev-env/dev-env-user-view.component').then(m => m.DevEnvUserViewComponent),
        title: 'Personal Environment - Nexus',
      },
      {
        path: 'audit-logs',
        loadComponent: () =>
          import('./features/audit-logs/audit-logs.component').then(m => m.AuditLogsComponent),
      },
      {
        path: 'security',
        canActivate: [securityPostureGuard],
        loadComponent: () =>
          import('./features/security-posture/security-posture.component').then(m => m.SecurityPostureComponent),
        title: 'Security Posture - Nexus',
      },
      {
        path: 'pricing',
        loadComponent: () =>
          import('./features/pricing/pricing.component').then(m => m.PricingComponent),
      },
      {
        path: 'billing',
        canActivate: [tenantOnlyGuard],
        loadComponent: () =>
          import('./features/billing/billing.component').then(m => m.BillingComponent),
      },
      {
        // Not tenant-scoped (available to platform admins too), so no tenantOnlyGuard -
        // the parent shell's authGuard + needsTenantGuard already protect it.
        path: 'settings',
        loadComponent: () =>
          import('./features/user-settings/user-settings.component').then(m => m.UserSettingsComponent),
        title: 'Settings - Nexus',
      },
      {
        path: 'platform-admin/access-requests',
        loadComponent: () =>
          import('./features/access-requests/access-requests.component').then(m => m.AccessRequestsComponent),
        canActivate: [authGuard, platformAdminGuard],
        title: 'Access Requests - Nexus',
      },
      {
        path: 'platform-admin/tiers',
        canActivate: [platformAdminGuard],
        loadComponent: () =>
          import('./features/platform-admin/tier-editor/tier-editor.component')
            .then(m => m.TierEditorComponent),
        title: 'Tier Configuration - Nexus',
      },
      {
        path: 'platform-admin/platform/environments',
        canActivate: [platformAdminGuard],
        loadComponent: () =>
          import('./features/platform-admin/environments/environments-admin.component')
            .then(m => m.EnvironmentsAdminComponent),
        title: 'Environments - Nexus',
      },
      {
        path: 'platform-admin/platform',
        canActivate: [platformAdminGuard],
        loadComponent: () =>
          import('./features/platform-admin/platform-overview/platform-overview.component')
            .then(m => m.PlatformOverviewComponent),
        title: 'Platform Overview - Nexus',
      },
      {
        path: 'platform-admin/tenants',
        canActivate: [platformAdminGuard],
        loadComponent: () =>
          import('./features/platform-admin/tenants/tenants.component')
            .then(m => m.TenantsComponent),
        title: 'Tenants - Nexus',
      },
      {
        path: 'platform-admin/promo-codes',
        canActivate: [platformAdminGuard],
        loadComponent: () =>
          import('./features/platform-admin/promo-codes/promo-codes.component')
            .then(m => m.PromoCodesComponent),
        title: 'Promo Codes - Nexus',
      },
      {
        path: 'platform-admin/announcements',
        canActivate: [platformAdminGuard],
        loadComponent: () =>
          import('./features/platform-admin/announcements-admin/announcements-admin.component')
            .then(m => m.AnnouncementsAdminComponent),
        title: 'Announcements - Nexus',
      },
      {
        path: 'platform-admin/feedback',
        canActivate: [platformAdminGuard],
        loadComponent: () =>
          import('./features/platform-admin/feedback-admin/feedback-admin.component')
            .then(m => m.FeedbackAdminComponent),
        title: 'Feedback - Nexus',
      },
      {
        path: 'platform-admin/platform-settings',
        canActivate: [platformAdminGuard],
        loadComponent: () =>
          import('./features/platform-admin/platform-settings/platform-settings.component')
            .then(m => m.PlatformSettingsComponent),
        title: 'Platform Settings - Nexus',
      },
      {
        path: 'platform/alerts',
        canActivate: [platformAdminGuard],
        loadComponent: () =>
          import('./features/platform-alerts/platform-alerts.component')
            .then(m => m.PlatformAlertsComponent),
        title: 'Alerts - Nexus',
      },
      {
        path: 'whats-new',
        canActivate: [tenantOnlyGuard],
        loadComponent: () =>
          import('./features/whats-new/whats-new.component')
            .then(m => m.WhatsNewComponent),
        title: "What's New - Nexus",
      },
      {
        path: 'feedback',
        loadComponent: () =>
          import('./features/feedback/my-feedback/my-feedback.component')
            .then(m => m.MyFeedbackComponent),
        title: 'My Feedback - Nexus',
      },
    ],
  },

  { path: '**', redirectTo: 'login' },
];
